import { Directive, ElementRef, Input, Renderer2, ViewContainerRef, TemplateRef } from '@angular/core';
import { UserAccessService } from '../services/user-access.service';
import { RolePermission } from '../models/Securable';

@Directive({
    selector: '[formSecurable]'
})
/**
 * This directive is to be wrapped in the evoSecurable directive.
 * Often times we have bootstrap organizing wrappers around our form inputs
 * This will enable/disable form inputs and will only be shown if it's 
 * not deleted by read access permissions on the wrapper
 */
export class FormSecurableDirective {
    private readonly disabledClass = 'disabledClass';
    private readonly debug: boolean = false;
    private ParentObjectID: number;
	/**
	 * Shorthand ID for the current attached element
	 * @returns Uppercase value of the current element ID
	 */
    get id(): string {
        return String(this.el.nativeElement.id).toUpperCase();
    }
	/**
	 * Shorthand for the current attached element
	 * @returns ElementRef.NativeElement
	 */
    get elem(): any {
        return this.el.nativeElement;
    }

    constructor(private aSvc: UserAccessService,
        private el: ElementRef,
        private renderer: Renderer2,
        private _viewContainer: ViewContainerRef,
    ) {
        this.disableElement();
    }
    @Input('formSecurable') formSecurable: string; //Enables taking a route
    @Input('Editing') Editing: boolean;
    @Input('MID') merchantID: number; //Optional merchant ID for specific permissions
    @Input('SEC') securableName: string; //Full name (without ESP|) of the securable to use instead of ID
    @Input('SEID') salesEntityID: number; //Optional Sales Entity ID for specific permissions
    @Input('SubID') subObjID: number; //Optional sub object ID when present must be sent along with the request for securables
    @Input('CHANGE') change: boolean; //Optional input to force change control to trigger can be flipped on/off (places where we can't use editing)
    
    /**
	 * Can't get input values until ngOnInit
	 */
    ngOnInit() {
        // Guard against no ID defined in the constructor
        if (!this.canShow()) {
            console.error('No ID defined for secured element:', this.el);
        }
        this.initializeElement();
    }

    ngOnChanges() {
        if (this.debug) { console.log("%cFormSecurables Change Detected ElementID,Editing: ", 'background-color:#ffc74f,color:black;', this.id, this.Editing); }
        this.initializeElement();
    }
	/**
	 * Initializes the element based on securable permission
	 */
    private async initializeElement() {
        //console.log("INIT()");
        //Use the explicit name if we have one
        let perm: RolePermission;
        if (this.securableName) {
            //This is the more likely of the two, I started passing the name explicitly
            let workingName = this.securableName.toUpperCase();
            this.aSvc.GetSecurableObjectByName(workingName, undefined, this.salesEntityID, this.merchantID, true).then(resp => {
                if (this.debug) { console.log("%cFormSecurables GotSecurableObject: ", 'background-color:#bcbcbc; color:black;', resp); }
                perm = resp;
                if (this.subObjID) {
                    if (this.debug) { console.log("%cFormSecurables HasSubObjID: ", 'background-color:#bcbcbc; color:black;', this.subObjID); }
                    //Must fetch sub object level permissions
                    this.FetchSubObjectSecurable(perm).then(specificResp => {
                        if (this.debug) { console.log("%cFormSecurables GotSecurableParent: ", 'background-color:#bcbcbc; color:black;', specificResp); }
                        //Adjust this element based on the SUB OBJ securable
                        this.AdjustElement(specificResp);
                    });
                } else {
                    //No sub object(s) adjust like normal
                    this.AdjustElement(perm);
                }
            });
        } else {
            //console.error("NO PROVIDED SECURABLE FULL NAME.");
            this.aSvc.GetSecurableObjectByName(this.id, this.formSecurable, this.salesEntityID, this.merchantID, false).then(resp => {
                if (this.debug) { console.log("%cFormSecurables GotSecurableObject: ", 'background-color:#bcbcbc; color:black;', resp); }
                perm = resp;
                if (this.subObjID) {
                    //Must fetch sub object level permissions
                    if (this.debug) { console.log("%cFormSecurables HasSubObjID: ", 'background-color:#bcbcbc; color:black;', this.subObjID); }
                    this.FetchSubObjectSecurable(perm).then(specificResp => {
                        //Adjust this element based on the SUB OBJ securable
                        if (this.debug) { console.log("%cFormSecurables GotSecurableParent: ", 'background-color:#bcbcbc; color:black;', specificResp); }
                        this.AdjustElement(specificResp);
                    });
                } else {
                    //No sub object(s) adjust like normal
                    this.AdjustElement(perm);
                }
            });
        }
    }
    /**
     * Takes the ParentID from the passed in permission and returns the new permissions for 
     * the sub object ID + ParentID from the securables_Get sproc
     * @param perm The original permission that gives us the ParentObjID
     */
    private async FetchSubObjectSecurable(perm: RolePermission): Promise<RolePermission> {
        if (!this.ParentObjectID) { this.ParentObjectID = perm.ObjectSecurableID; }
        if (this.securableName) {
            return await this.aSvc.GetSecurableObjectByName(this.securableName.toUpperCase(), undefined, this.salesEntityID, this.merchantID, true, this.subObjID, this.ParentObjectID);
        } else {
            console.warn("******* IT IS STRONGLY RECOMMENDED DEVELOPER PASSES [SEC] INTO SECURABLES *********", perm.SecurableName);
            return await this.aSvc.GetSecurableObjectByName(this.id, this.formSecurable, this.salesEntityID, this.merchantID, false, this.subObjID, this.ParentObjectID);
        }
    }

    private AdjustElement(perm: RolePermission) {
        if (perm === undefined) {
            //Permission is undefined keep it disabled
            if (this.debug) { console.log("%cFormSecurables Disabled Element (Permission undefined): ", 'background-color:#bcbcbc;color:red;', this.securableName, this.id, this.Editing); }
            this.disableElement();
        } else {
            //console.log("Permission Obj, this.editing, this.salesentity: ", perm, this.Editing, this.salesEntityID);
            if (perm.AccessLevel > 1 && this.Editing && !perm.HasPendingChange) {
                // If they can write it and are in edit mode already enable it
                if (this.debug) { console.log("%cFormSecurables ENABLED ELEMENT:", 'background:#129147;color:white;', this.securableName); }
                this.enableElement();
            } else {
                if (this.debug) { console.log("%cFormSecurables Disabled Element: ", 'background-color:#bcbcbc;color:red;', this.securableName, this.id, this.Editing); }
                this.disableElement();
            }
        }

    }
    /**
     * @returns True if the ID of this element is defined.
     */
    private canShow(): boolean {
        if ((this.id === '' || this.id === undefined) && (this.securableName === '' || this.securableName === undefined)) {
            if (this.debug) {
                console.warn("%cFormSecurables No ID AND No Securable Name", 'background-color:white;color:red;', this.id, this.securableName);
            }
            return false;
        }
        return true;
    }

    /**
    * Disables the current attached element (by adding the disabled class)
    */
    private disableElement() {
        this.renderer.addClass(this.elem, this.disabledClass);
        this.elem.disabled = true;
    }

	/**
	* Enables the current attached element (by removing the disabled class)
	*/
    private enableElement() {
        this.renderer.removeClass(this.elem, this.disabledClass);
        this.elem.disabled = false;
    }
}